import { useState, useRef, RefObject } from "react";
import {
  Button,
  Modal,
  DateInput,
  SelectBox,
  TextInput,
  TextAreaInput,
  Grid,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu.js";

import { usePermissions } from "@/hooks/usePermissions";
import { useSnackbar } from "notistack";
import { getIpAddress } from "@/api";
import { db_timestamp } from "@/utils/date-format";
import { cocTypeOptions } from "../Coc.types";

import type { BaseCocModalProps, CocSignatureObject } from "../Coc.types";
import EvidenceCocAPI from "@/api/evidence/coc";
import { createSignature } from "../utils";
import { SignatureField, SignatureFieldRef } from "@/Features";

interface InsertCocModalProps extends BaseCocModalProps {}

interface InsertFormData {
  custody_from: string;
  custody_to: string;
  timestamp: string;
  coc_type: string;
  reason: string;
}

const insertValidationSchema = z.object({
  custody_from: z
    .string({
      required_error: "Custody from is required",
    })
    .min(1, { message: "Custody from is required" }),
  custody_to: z
    .string({
      required_error: "Custody to is required",
    })
    .min(1, { message: "Custody to is required" }),
  timestamp: z
    .string({
      required_error: "Timestamp is required",
    })
    .min(1, { message: "Timestamp is required" }),
  coc_type: z
    .string({
      required_error: "COC Type is required",
    })
    .min(1, { message: "COC Type is required" }),
  reason: z
    .string({
      required_error: "COC Notes are required",
    })
    .min(1, { message: "COC Notes are required" }),
});

const InsertCocModal: React.FC<InsertCocModalProps> = ({
  show,
  onClose,
  onSubmit,
  defaultInfo,
}) => {
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<InsertFormData>({
    mode: "uncontrolled",
    initialValues: {
      custody_from: "",
      custody_to: "",
      timestamp: new Date().toISOString(),
      coc_type: "",
      reason: "",
    },
  });

  const { currentUser } = usePermissions();
  const { enqueueSnackbar } = useSnackbar();

  const custodyToSig = useRef<SignatureFieldRef>();
  const custodyFromSig = useRef<SignatureFieldRef>();

  const handleSubmit = async () => {
    const formData = form.getValues();

    const validationResult = await validateAsync(insertValidationSchema)(
      formData
    );
    if (validationResult?.hasErrors) {
      console.error("Validation Errors", validationResult?.errors);
      form.setErrors(validationResult?.errors);
      return;
    }

    setSubmitting(true);
    try {
      const ipAddress = await getIpAddress();

      const signature: CocSignatureObject = {
        custody_from_sig: createSignature(
          custodyFromSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
        custody_to_sig: createSignature(
          custodyToSig as RefObject<SignatureFieldRef>,
          ipAddress,
          currentUser
        ),
      };

      const cocRecordData = {
        evidence_id: defaultInfo.evidence_id,
        custody_to: formData.custody_to,
        custody_from: formData.custody_from,
        reason: formData.reason || "Chain of Custody Record Created",
        entered_by: currentUser.full_name,
        timestamp: db_timestamp(formData.timestamp, true),
        signature: JSON.stringify(signature),
        coc_type: formData.coc_type || null,
      };

      await EvidenceCocAPI.createCocRecord(cocRecordData);

      enqueueSnackbar(`Chain of Custody Record Inserted`, {
        variant: "success",
      });

      onSubmit?.(cocRecordData);
    } catch (error) {
      console.error("Error creating CoC record:", error);
      enqueueSnackbar(`Error creating chain of custody record.`, {
        variant: "error",
      });
    } finally {
      setSubmitting(false);
      onClose?.();
    }
  };

  const handleCancel = () => {
    form.reset();
    onClose?.();
  };

  return (
    <Modal open={show} onClose={onClose} style={{ width: 600 }}>
      <Modal.Title>Create Chain of Custody Record</Modal.Title>
      <div style={{ margin: "20px 0px" }}>
        <Grid col={1}>
          <TextInput
            label="Custody From"
            required
            placeholder="Enter custody from..."
            key={form.key("custody_from")}
            {...form.getInputProps("custody_from")}
          />
          <TextInput
            label="Custody To"
            required
            placeholder="Enter custody to..."
            key={form.key("custody_to")}
            {...form.getInputProps("custody_to")}
          />
          <DateInput
            label="Timestamp"
            required
            format="YYYY-MM-DD HH:mm:ss"
            enableCalendar
            arrow
            key={form.key("timestamp")}
            {...form.getInputProps("timestamp")}
          />
          <SelectBox
            label="COC Type"
            required
            placeholder="Select COC type..."
            data={cocTypeOptions}
            key={form.key("coc_type")}
            {...form.getInputProps("coc_type")}
          />
          <TextAreaInput
            label="COC Notes"
            required
            minRows={3}
            key={form.key("reason")}
            {...form.getInputProps("reason")}
          />
          <div>
            <SignatureField
              sigFieldRef={custodyFromSig}
              label="Custody From Signature"
              useESignature={true}
            />
          </div>
          <div>
            <SignatureField
              sigFieldRef={custodyToSig}
              label="Custody To Signature"
              useESignature={true}
            />
          </div>
        </Grid>
      </div>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={handleCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Create Record
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default InsertCocModal;
